// PWA install-prompt hook.
//
// Captures the browser's `beforeinstallprompt` event so the app can surface
// an in-UI install button rather than relying on the browser's own banner.
// The hook is a no-op on platforms that never fire that event (e.g. iOS
// Safari, desktop browsers without PWA support) — callers guard on
// `canInstall` before rendering anything.

import { useEffect, useRef, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

interface UsePWAInstall {
  /** True when the browser has staged a deferred install prompt and the app
   *  is not already running in standalone mode. */
  canInstall: boolean;
  /** True once the app has been installed (either pre-existing standalone
   *  launch or after the user accepts the install prompt). */
  isInstalled: boolean;
  /** Call from a user-gesture handler (e.g. button onClick) to show the
   *  browser's native install dialog. Resolves after the user's choice. */
  triggerInstall: () => Promise<void>;
}

export function useInstallPWA(): UsePWAInstall {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Keep a ref so the stable `triggerInstall` closure can read the latest
  // deferred prompt without re-creating on every state update.
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);
  promptRef.current = deferredPrompt;

  useEffect(() => {
    // Detect standalone launch (already installed).
    // `navigator.standalone` is an Apple extension (iOS Safari); it is not in
    // the standard Navigator type, so we cast via `window.navigator` with a
    // narrow inline type to avoid the `no-undef` ESLint rule firing on the
    // bare `Navigator` global.
    const nav = window.navigator as typeof window.navigator & { standalone?: boolean };
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      nav.standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    function handleBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    function handleAppInstalled() {
      setIsInstalled(true);
      setDeferredPrompt(null);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  async function triggerInstall(): Promise<void> {
    const prompt = promptRef.current;
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice;
    setDeferredPrompt(null);
  }

  return {
    canInstall: deferredPrompt !== null && !isInstalled,
    isInstalled,
    triggerInstall,
  };
}
